import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { connect, useDispatch, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router';
import { PARAMETERS } from '@root/tracking/constants';
import {
  Card,
  OnlyInDesktopTablet,
  OnlyInMobile,
  ExpandableDescription,
  VideoPlayer,
  AddToSavedItemsButton,
  Notification,
} from '@components/shared';
import { Row, Col } from '@components/antd';
import routes from '@root/routes';
import { Landlord } from '@root/types';
import {
  galleryNavigated,
  landlordThumbnailClicked,
  savedBuildingOwnerNotificationLinkClicked,
} from '@store/actions/landlordPage';
import rootActions from '@store/actions';
import useMarketo from '@root/shared/useMarketo';
import useAltText from '@root/shared/useAltText';
import useAnalytics from '@shared/useAnalytics';
import { get, create, destroy } from '@shared/typedApi';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useCurrentUser from '@root/shared/useCurrentUser';
import NotFound from '../404/NotFound';
import BasicInfo from './DeprecatedBasicInfo';
import Portfolio from './Portfolio';
import Layout from '../Layout';
import RawImages from '../Building/Images/RawImages';
import LandlordHead from './LandlordHead';
import s from './DeprecatedLandlordWithoutBrandedAssets.module.less';
import LandlordSkeleton from './LandlordSkeleton';

const mapDispatch = {
  savedBuilingOwnerLinkClicked: () => savedBuildingOwnerNotificationLinkClicked,
};

const connector = connect(null, mapDispatch);
type ReduxProps = ConnectedProps<typeof connector>;

export const RawLandlord = ({ savedBuilingOwnerLinkClicked }: ReduxProps) => {
  const { currentUser } = useCurrentUser();
  const hasCurrentUser = !!currentUser;
  const { t } = useTranslation('landlord');
  const { getAltTextForCompanyLogo } = useAltText();
  const dispatch = useDispatch();
  const { slug } = useParams<{ slug: string }>();
  const client = useQueryClient();
  const { saveInteraction, pageLoaded } = useAnalytics();

  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const {
    data: landlord,
    isLoading,
    error,
  } = useQuery<Landlord>([routes.api.landlord(slug)], () => get(routes.api.landlord(slug)), {
    onSuccess: async fetchedLandlord => {
      dispatch(rootActions.setLandlordPage(fetchedLandlord.analyticsInformation));
      if (!hasLoadedOnce) {
        pageLoaded({
          pageName: PARAMETERS.landlordPage,
          actionType: 'PAGE_LOADED:LANDLORD',
        });
        setHasLoadedOnce(true);
      }
      window.prerenderReady = true;
    },
  });

  useMarketo();

  const { data: isLandlordSaved } = useQuery<boolean>(
    [routes.api.buildingOwnerSave(slug)],
    () => get(routes.api.buildingOwnerSave(slug)),
    { enabled: hasCurrentUser },
  );
  const toggleSaveMutation = useMutation(
    async (shouldBeSaved: boolean) => {
      if (shouldBeSaved) {
        await create(routes.api.buildingOwnerSave(slug), {});
      } else {
        await destroy(routes.api.buildingOwnerSave(slug), {});
      }
    },
    {
      onSuccess: (_, wasSaved) => {
        saveInteraction({
          sourcePage: PARAMETERS.landlordPage,
          action: wasSaved ? PARAMETERS.saveItem : PARAMETERS.unsaveItem,
        });
        client.invalidateQueries([routes.api.buildingOwnerSave(slug)]);
        if (wasSaved) {
          Notification.saveSuccess({
            title: t('landlord:saved'),
            /* eslint-disable react/jsx-no-literals */
            text: (
              <Trans i18nKey="savedSuccessNotification" ns="landlord">
                You’ll get notified if a listing is available from this building owner. View or edit
                it in your
                <a href={routes.savedBuildingOwners} onClick={savedBuilingOwnerLinkClicked}>
                  saved building owners
                </a>
                section.
              </Trans>
            ),
            /* eslint-enable react/jsx-no-literals */
          });
        }
      },
    },
  );

  if (isLoading) {
    return (
      <Layout>
        <LandlordSkeleton />
      </Layout>
    );
  }

  if (error) {
    return <NotFound />;
  }

  return landlord ? (
    <Layout showFooter={!!landlord}>
      <LandlordHead landlord={landlord} />
      <div
        className={s.headerContainer}
        data-testid={`layout-${hasLoadedOnce ? 'fullyLoaded' : 'notFullyLoaded'}`}
      >
        <h1 className={s.name}>{landlord.name}</h1>
        <div className={s.spacer}></div>
        {/* eslint-disable no-nested-ternary */}
        {hasCurrentUser ? (
          <div className={s.logoAndSaved}>
            {landlord.logo ? (
              <>
                <img
                  className={s.logo}
                  src={landlord.logo.mediumPath}
                  alt={getAltTextForCompanyLogo(landlord.name)}
                />
                <span className={s.headerDivider} />
              </>
            ) : null}
            <div className={s.savedButtonContainer} data-testid="save-building-owner">
              <AddToSavedItemsButton
                isSaved={!!isLandlordSaved}
                onClick={async (shouldBeSaved: boolean) => {
                  toggleSaveMutation.mutate(shouldBeSaved);
                }}
              />
            </div>
          </div>
        ) : landlord.logo ? (
          <img className={s.logo} src={landlord.logo.mediumPath} alt={landlord.logo.description} />
        ) : null}
        {/* eslint-enable no-nested-ternary */}
      </div>
      <OnlyInDesktopTablet>
        <div className={s.line}></div>
        <BasicInfo
          inventory={landlord.inventory}
          premierRetailDestinations={landlord.premierRetailDestinations}
          space={landlord.space}
          underConstruction={landlord.underConstruction}
        />
      </OnlyInDesktopTablet>
      <div className={s.mediaAssets}>
        <RawImages
          photos={landlord.photos}
          video={landlord.video}
          lightboxPaginationClicked={() => dispatch(galleryNavigated)}
          lightboxThumbnailClicked={() => dispatch(landlordThumbnailClicked)}
          sourcePage={PARAMETERS.landlordPage}
          sourceContent={PARAMETERS.brandGallery}
          firstPhotoOrientation={landlord.firstPhotoOrientation}
          imagesZipUrl={routes.api.landlordImagesZipUrl(landlord.slug)}
          companyName={landlord.name}
        />
      </div>
      <OnlyInMobile>
        <BasicInfo
          inventory={landlord.inventory}
          premierRetailDestinations={landlord.premierRetailDestinations}
          space={landlord.space}
          underConstruction={landlord.underConstruction}
        />
        <div className={s.line}></div>
      </OnlyInMobile>
      <Row gutter={16}>
        <Col lg={16} md={16} sm={24}>
          {/* eslint-disable react/no-danger */}
          <ExpandableDescription>
            <div
              className={s.description}
              dangerouslySetInnerHTML={{ __html: landlord.description }}
            />
          </ExpandableDescription>
          {/* eslint-enable react/no-danger */}
          {landlord.video ? (
            <OnlyInMobile>
              <VideoPlayer
                video={landlord.video}
                className={s.mobileVideo}
                sourcePage={PARAMETERS.landlordPage}
                sourceContent={PARAMETERS.spotlightTour}
                testId="mobileVideo"
              />
            </OnlyInMobile>
          ) : null}
        </Col>
        {landlord.executives && landlord.executives.length > 0 ? (
          <Col className={s.cardColumn} lg={8} md={8} sm={24}>
            <Card title={t('keyExecutives')}>
              <ul className={s.keyExecutiveList}>
                {landlord.executives.map(executive => (
                  <li key={executive}>{executive}</li>
                ))}
              </ul>
            </Card>
          </Col>
        ) : null}
      </Row>
      <Portfolio landlordSlug={landlord.slug} marketSlugs={landlord.marketSlugs} />
    </Layout>
  ) : (
    <NotFound />
  );
};

export default connector(RawLandlord);
